import React, { useEffect, useState,useContext } from "react";
import { AppContext } from "../../context/AppContext";

interface PasswordRequirements {
  minLength: number;
  maxLength: number;
  contains: {
    lowerCase: string;
    upperCase: string;
    number: string;
    symbol: string;
  };
  symbols: string;
}

const PasswordStrengthComponent = ({ password }: { password: string }) => {
  const { services :{
    serviceSystem
  }, I18N } = useContext(AppContext);

  const [passwordRequirements, setPasswordRequirements] = useState<PasswordRequirements | null>(null);
  const [strength, setStrength] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    // the component is unmounted with the tab it sits in, and the policy request
    // can outlive it; without this guard the late answer would set state on a
    // gone component and, worse, resurrect an error message on a fresh mount
    let active = true;

    const fetchPasswordRequirements = async () => {
      try {
        const response = await serviceSystem.getPasswordRequirements();

        if (!active) {
          return;
        }

        setPasswordRequirements(response);
      } catch (error) {
        if (!active) {
          return;
        }

        console.error("Fehler beim Abrufen der Passwortanforderungen:", error);
        setErrorMessage(I18N.t("Fehler beim Abrufen der Passwortanforderungen"));
      }
    };

    fetchPasswordRequirements();

    return () => {
      active = false;
    };
    // I18N and serviceSystem are stable for a session; the policy is fetched once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    if (passwordRequirements) {
      let calculatedStrength = 0;

      const { minLength, maxLength, contains, symbols } = passwordRequirements;

      if (password.length >= minLength && password.length <= maxLength) calculatedStrength += 1;

      if (contains.lowerCase && /[a-z]/.test(password)) calculatedStrength += 1;

      if (contains.upperCase && /[A-Z]/.test(password)) calculatedStrength += 1;

      if (contains.number && /[0-9]/.test(password)) calculatedStrength += 1;

      if (contains.symbol && symbols.split('').some(char => password.includes(char))) calculatedStrength += 1;
      //console.log("Calculated Strength:", calculatedStrength);
      setStrength(calculatedStrength);
    }
  }, [password, passwordRequirements]);

  // the four verdicts used to be german string literals and never reached the
  // translation files, so an english or french session read "sehr stark"
  const getStrengthMessage = (strength: number) => {
    switch (strength) {
      case 0:
      case 1:
      case 2:
        return { message: I18N.t('schwach'), color: 'red' };
      case 3:
        return { message: I18N.t('mittel'), color: 'orange' };
      case 4:
        return { message: I18N.t('stark'), color: 'yellowgreen' };
      case 5:
        return { message: I18N.t('sehr stark'), color: 'green' };
      default:
        return { message: '', color: '' };
    }
  };

  const { message, color } = getStrengthMessage(strength);

  if (errorMessage) {
    return <p role="alert" data-password-strength-error>{errorMessage}</p>;
  }

  return (
    <dia-form>
      <dia-form-area>
        { /* the bar is decoration for the verdict next to it and carries no
             information of its own, so it stays out of the accessibility tree */ }
        <dia-form-control
          aria-hidden="true"
          style={{ width: '100%', height: '6px', background: color }}
        ></dia-form-control>
        { /* the verdict changes while typing and is announced politely, so it
             never cuts into the character the user just entered */ }
        <dia-form-control has={color} aria-live="polite" data-password-strength-verdict>
          {message}
        </dia-form-control>
      </dia-form-area>
    </dia-form>
  );
};

export default PasswordStrengthComponent;
